import type { ServiceProviderHourlyRate, ServiceProviderPricing } from '../types/firestore';

export const SERVICE_HOURLY_RATE_MAX_AMOUNT = 9999;

const SERVICE_HOURLY_RATE_INPUT_PATTERN = /^\d+(?:[.,]\d{1,2})?$/;

type ServicePricingSource =
  | {
      servicePricing?: ServiceProviderPricing | null;
    }
  | null
  | undefined;

export type PublicServiceHourlyRate = ServiceProviderHourlyRate & {
  public: true;
  amount: number;
  currency: 'PLN';
};

export const parseServiceHourlyRateAmountInput = (
  value: unknown
): { amount?: number; error?: string } => {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return { error: 'Podaj poprawną kwotę.' };
    }
    const rounded = Number(value.toFixed(2));
    if (rounded <= 0) {
      return { error: 'Stawka musi być większa od 0 zł/h.' };
    }
    if (rounded > SERVICE_HOURLY_RATE_MAX_AMOUNT) {
      return { error: `Maksymalna stawka to ${SERVICE_HOURLY_RATE_MAX_AMOUNT} zł/h.` };
    }
    return { amount: rounded };
  }

  const normalized = String(value ?? '')
    .trim()
    .replace(/\s+/g, '');
  if (!normalized) {
    return { error: 'Podaj stawkę orientacyjną.' };
  }
  if (!SERVICE_HOURLY_RATE_INPUT_PATTERN.test(normalized)) {
    return { error: 'Użyj kwoty do 9999 zł/h, maksymalnie z dwoma miejscami po przecinku.' };
  }

  const parsed = Number(normalized.replace(',', '.'));
  if (!Number.isFinite(parsed)) {
    return { error: 'Podaj poprawną kwotę.' };
  }

  const rounded = Number(parsed.toFixed(2));
  if (rounded <= 0) {
    return { error: 'Stawka musi być większa od 0 zł/h.' };
  }
  if (rounded > SERVICE_HOURLY_RATE_MAX_AMOUNT) {
    return { error: `Maksymalna stawka to ${SERVICE_HOURLY_RATE_MAX_AMOUNT} zł/h.` };
  }

  return { amount: rounded };
};

export const isValidPublicServiceHourlyRate = (
  value: unknown
): value is PublicServiceHourlyRate => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  if (candidate.public !== true) return false;
  if (candidate.currency !== 'PLN') return false;
  const amount = candidate.amount;
  if (typeof amount !== 'number' || !Number.isFinite(amount)) return false;
  return amount > 0 && amount <= SERVICE_HOURLY_RATE_MAX_AMOUNT;
};

export const getPublicServiceHourlyRate = (
  profile: ServicePricingSource
): PublicServiceHourlyRate | null => {
  const hourlyRate = profile?.servicePricing?.hourlyRate;
  return isValidPublicServiceHourlyRate(hourlyRate) ? hourlyRate : null;
};

export const formatServiceHourlyRateAmount = (amount: number): string => {
  const hasFraction = Math.abs(amount % 1) > 0.0001;
  return new Intl.NumberFormat('pl-PL', {
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formatServiceHourlyRateLabel = (amount: number): string =>
  `od ${formatServiceHourlyRateAmount(amount)} zł/h`;
